import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NameDisclosureList } from "../../../components/UIToolkit/molecules/NameDisclosureList";
import type { AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import { peopleOf } from "./customPrintoutPeople";

/** Up to this many missing people are listed straight away; more sit behind "Show all". */
const MISSING_SHOWN_OPEN_MAX = 6;

type Props = {
	/** The day's shifts the page leaves out. Named here, in the browser — names never leave it. */
	missingShifts: AiPrintoutShift[];
};

/** Who a custom printout would leave off the paper: each person once, with all of their missing shifts' times. */
export const CustomPrintoutMissingPeople: FC<Props> = ({ missingShifts }) => {
	const { t } = useTranslation();
	const people = useMemo(() => peopleOf(missingShifts), [missingShifts]);
	const [isOpen, setIsOpen] = useState<boolean>(people.length <= MISSING_SHOWN_OPEN_MAX);
	return (
		<>
			{t("printout.custom.ai.incomplete.missing", { count: people.length })}
			<NameDisclosureList
				dense
				open={isOpen}
				onToggle={() => setIsOpen((open) => !open)}
				showLabel={t("printout.custom.ai.incomplete.show", { count: people.length })}
				hideLabel={t("printout.custom.ai.incomplete.hide")}
				sections={[
					{
						id: "missing",
						items: people.map((person) => ({
							id: person.nurseId,
							label: t("printout.custom.ai.incomplete.person", { name: person.name, time: person.timeLabels.join(", ") }),
						})),
					},
				]}
				dataTestId="custom-printout-missing-list"
				itemTestId={(item) => `custom-printout-missing-${item.id}`}
			/>
		</>
	);
};
